import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/solid";
import debounce from "lodash/debounce";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { twMerge } from "tailwind-merge";

import type { ModalTheme } from "@/components/types";
import { useTutorial } from "@/lib/zustand/useTutorial/useTutorial";
import { classNames } from "@/utils/classNames";

function UnmemoizedSearchInput({
  searchString,
  setSearchString,
  placeholder,
  small,
  open,
  className,
  theme = "light",
}: {
  searchString: string;
  setSearchString: (value: string) => void;
  placeholder: string;
  small: boolean;
  open: boolean | null;
  className?: string;
  theme?: ModalTheme;
}) {
  const [localSearchString, setLocalSearchString] = useState(searchString);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedSetSearchString = useCallback(
    debounce((value: string) => {
      setSearchString(value);
    }, 300),
    [setSearchString]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalSearchString(value);
      debouncedSetSearchString(value);
    },
    [debouncedSetSearchString]
  );

  const handleClearSearch = useCallback(() => {
    setLocalSearchString("");
    setSearchString("");
    debouncedSetSearchString.cancel();
    if (inputRef.current !== null) {
      inputRef.current.focus();
    }
  }, [debouncedSetSearchString, setSearchString]);

  useEffect(() => {
    if (open !== null && open) {
      if (inputRef.current !== null) {
        // Don't focus on mobile or tablet since that opens the keyboard
        if (!isMobile && !isTablet) {
          inputRef.current.focus();
        }
      }
    }
  }, [open]);

  useEffect(() => {
    if (open !== null && open) {
      setLocalSearchString("");
      setSearchString("");
    }
  }, [open]);

  const isActive = useTutorial((state) => state.isActive);

  return (
    <div className={twMerge("w-full", className)}>
      <label htmlFor="search" className="sr-only">
        Search
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          name="search"
          id="search"
          className={classNames(
            "block w-full rounded-full border-0 pl-12 pr-12 shadow-sm ring-1 ring-inset",
            // Dark: the field steps up from the panel, borderless (same recipe as the TTV dialogs' inputs), rather than a near-black well or a white glow in the middle of it.
            theme === "dark" ?
              "bg-ttvRaised text-white ring-transparent placeholder:text-ttvDim focus:ring-ttvLine"
            : "text-gray-900 ring-primary placeholder:text-gray-400",
            small ? "py-2" : "py-4"
          )}
          placeholder={placeholder}
          value={localSearchString}
          onChange={handleSearchChange}
          autoComplete="off"
          // Disable search input while tutorial is active, since the tutorial takes care of showing the appropriate artist
          disabled={isActive}
        />
        <div className="pointer-events-none absolute bottom-0 left-0 top-0 flex items-center pl-4">
          <MagnifyingGlassIcon className="h-6 w-6 shrink-0 fill-gray-400" />
        </div>
        {localSearchString !== "" && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="absolute bottom-0 right-0 top-0 flex items-center pr-4 transition-opacity hover:opacity-75"
            aria-label="Clear search"
          >
            <XMarkIcon className="h-5 w-5 shrink-0 fill-gray-400" />
          </button>
        )}
      </div>
    </div>
  );
}

export const SearchInput = memo(UnmemoizedSearchInput);
